import type { IFlagsmith } from 'flagsmith';
import { LOCAL_FEATURE_FLAGS, flagsmithConfig } from '@config';
import { webappClient } from './bugsnag';

type User = {
  id: string;
  workspaceId: string;
  organizationId: string;
  isNewUser?: boolean;
  planType?: string;
  environment?: string;
};

export enum FeatureFlagKeys {
  'deviceModeTransformations' = 'device-mode-transformations-web',
  'rs360EnableWht' = 'rs360-enable-wht',
  'text2sql' = 'text-2-sql',
  'mtuUsage' = 'mtu-usage',
  'enableCohortAudience' = 'enable-cohort-audience',
  'enableDataCatalogCustomTypes' = 'enable-data-catalog-custom-types',
  'enabledDataCatalogTPWorkspaceCopy' = 'enable-data-catalog-tp-workspace-copy',
  'reportingDelay' = 'reporting-delay',
  'barGraphForWarehouseDest' = 'bar-graph-for-warehouse-dest',
  'enableDataCatalogVariants' = 'enable-data-catalog-variants',
  'enableRetlCursorColumn' = 'enable-retl-cursor-column',
  'enableDestinationEventDetails' = 'obs-enable-destination-event-details',
  'enableDeliveredWithWarnings' = 'enable-delivered-with-warnings',
  'trackingPlanValidateStageInUTLiveEvents' = 'tracking-plan-validate-stage-ut-live-event',
  'enableNewPermissionsModel' = 'enable-new-permissions-model',
  'enableNewPermissionsModelEnforcement' = 'enable-new-permissions-model-enforcement',
  'enableRetlFullSync' = 'enable-retl-full-sync',
  'enableBotBlocking' = 'enable-bot-blocking',
  'enableEventBlocking' = 'enable-event-blocking',
  'enableTrackingPlanValidationInSourceLiveEvents' = 'enable-tracking-plan-validation-in-source-live-events',
  'enableProfilesCopilot' = 'enable-profiles-copilot',
  'enableDataMapper' = 'enable-data-mapper',
  'enableTransformationDraftMode' = 'enable-transformations-draft-mode',
  'enableETLSunset' = 'enable-etl-sunset',
  'enableTransformationsTests' = 'enable-transformations-tests',
  'enableDataGraph' = 'enable-data-graph',
  'enableTransformationsImpactTests' = 'enable-transformations-impact-tests',
  'enableDataGraphAudiences' = 'enable-data-graph-audiences',
  'enableWorkspaceTokens' = 'enable-workspace-tokens',
  'enableCodeServerTokenExchange' = 'enable-code-server-token-exchange',
  'enablePlgBillingV2' = 'enable-plg-billing-v2',
  'enablePlgTrial' = 'enable-plg-trial',
  'enablePlgLockedResources' = 'enable-plg-locked-resources',
  'enablePlgTrialExistingUsers' = 'enable-plg-trial-existing-users',
  'enableEuSupport' = 'enable-eu-support',
  'enableTotpMfa' = 'enable-totp-mfa',
  'enableProfilesOutputSchema' = 'enable-profiles-output-schema',
  'enablePlgOnboardingV2' = 'enable-plg-onboarding-v2',
  'enablePlgTrialOnSignUp' = 'enable-plg-trial-on-signup',
  'enableGoogleWorkspaceScim' = 'enable-google-workspace-scim',
  'enableGroupMapping' = 'enable-group-mapping',
  'enableCalendlyScheduler' = 'enable-calendly-scheduler',
  'enableRudderAiWebChat' = 'enable-rudder-ai-web-chat',
  'enablePaymentRetries' = 'enable-payment-retries',
  'enableBillingCoupons' = 'enable-billing-coupons',
}

type FeatureFlagsWithPayload = {
  [K in keyof typeof featureFlagsDefaults]: 'payload' extends keyof (typeof featureFlagsDefaults)[K]
    ? K
    : never;
}[keyof typeof featureFlagsDefaults];

type FeatureFlagPayloads = {
  [K in FeatureFlagsWithPayload]: (typeof featureFlagsDefaults)[K]['payload'];
};

// List of destination feature flags
export enum DestinationFeatureFlags {
  'vdmNext' = 'vdm-next',
}

type DestinationFeatureFlagDefaults = {
  [K in DestinationFeatureFlags]: {
    payload: Record<string, boolean>;
    default: boolean;
  };
};

type FlagsmithFeatureValues = {
  [K in FeatureFlagsWithPayload]: FeatureFlagPayloads[K];
} & {
  [K in Exclude<FeatureFlagKeys, FeatureFlagsWithPayload>]: string | number | boolean | null;
} & {
  [K in DestinationFeatureFlags]: Record<string, boolean>;
};

// ts-prune-ignore-next
export const destinationFeatureFlagKeys = Object.values(DestinationFeatureFlags);
// ts-prune-ignore-next
export const featureFlagKeys = Object.values(FeatureFlagKeys);

const destinationFeatureFlagsDefaults: DestinationFeatureFlagDefaults = {
  [DestinationFeatureFlags.vdmNext]: {
    payload: {},
    default: false,
  },
};

// ts-prune-ignore-next
export const featureFlagsDefaults = {
  [FeatureFlagKeys.deviceModeTransformations]: {
    default: false,
  },
  [FeatureFlagKeys.rs360EnableWht]: {
    default: LOCAL_FEATURE_FLAGS.RS360_ENABLE_WHT === 'true',
  },
  [FeatureFlagKeys.text2sql]: {
    default: false,
  },
  [FeatureFlagKeys.mtuUsage]: {
    default: false,
  },
  [FeatureFlagKeys.enableCohortAudience]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_COHORT_AUDIENCE === 'true',
  },
  [FeatureFlagKeys.enableDataCatalogCustomTypes]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_DATA_CATALOG_CUSTOM_TYPES === 'true',
  },
  [FeatureFlagKeys.enabledDataCatalogTPWorkspaceCopy]: {
    default: false,
  },
  [FeatureFlagKeys.reportingDelay]: {
    payload: {
      delayInMinutes: 0,
    },
    default: false,
  },
  [FeatureFlagKeys.barGraphForWarehouseDest]: {
    default: false,
  },
  [FeatureFlagKeys.enableDataCatalogVariants]: {
    default: false,
  },
  [FeatureFlagKeys.enableRetlCursorColumn]: {
    default: false,
  },
  [FeatureFlagKeys.enableDestinationEventDetails]: {
    default: false,
  },
  [FeatureFlagKeys.enableDeliveredWithWarnings]: {
    default: false,
  },
  [FeatureFlagKeys.trackingPlanValidateStageInUTLiveEvents]: {
    default: false,
  },
  [FeatureFlagKeys.enableNewPermissionsModel]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_NEW_PERMISSIONS_MODEL === 'true',
  },
  [FeatureFlagKeys.enableNewPermissionsModelEnforcement]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_NEW_PERMISSIONS_MODEL_ENFORCEMENT === 'true',
  },
  [FeatureFlagKeys.enableRudderAiWebChat]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_RUDDER_AI_WEB_CHAT === 'true',
  },
  [FeatureFlagKeys.enableRetlFullSync]: {
    default: false,
  },
  [FeatureFlagKeys.enableBotBlocking]: {
    default: false,
  },
  [FeatureFlagKeys.enableEventBlocking]: {
    default: false,
  },
  [FeatureFlagKeys.enableTrackingPlanValidationInSourceLiveEvents]: {
    default: false,
  },
  [FeatureFlagKeys.enableProfilesCopilot]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_PROFILES_COPILOT === 'true',
  },
  [FeatureFlagKeys.enableDataMapper]: {
    default: false,
  },
  [FeatureFlagKeys.enableTransformationDraftMode]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_TRANSFORMATION_DRAFT_MODE === 'true',
  },
  [FeatureFlagKeys.enableETLSunset]: {
    default: false,
    payload: {
      etlSunsetDate: '2025-12-01',
      showETLSunsetAlert: false,
      useAllowlistForCreation: false,
    },
  },
  [FeatureFlagKeys.enableTransformationsTests]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_TRANSFORMATIONS_TESTS === 'true',
  },
  [FeatureFlagKeys.enableDataGraph]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_DATA_GRAPH === 'true',
  },
  [FeatureFlagKeys.enableTransformationsImpactTests]: {
    default: false,
  },
  [FeatureFlagKeys.enableDataGraphAudiences]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_DATA_GRAPH_AUDIENCES === 'true',
  },
  [FeatureFlagKeys.enableWorkspaceTokens]: {
    default: false,
  },
  [FeatureFlagKeys.enableCodeServerTokenExchange]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_CODE_SERVER_TOKEN_EXCHANGE === 'true',
  },
  [FeatureFlagKeys.enablePlgBillingV2]: {
    default: false,
  },
  [FeatureFlagKeys.enablePlgTrial]: {
    default: false,
  },
  [FeatureFlagKeys.enableEuSupport]: {
    default: false,
  },
  [FeatureFlagKeys.enablePlgLockedResources]: {
    default: false,
  },
  [FeatureFlagKeys.enableTotpMfa]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_TOTP_MFA === 'true',
  },
  [FeatureFlagKeys.enableProfilesOutputSchema]: {
    default: LOCAL_FEATURE_FLAGS.ENABLE_PROFILES_OUTPUT_SCHEMA === 'true',
  },
  [FeatureFlagKeys.enablePlgTrialExistingUsers]: {
    default: false,
  },
  [FeatureFlagKeys.enablePlgOnboardingV2]: {
    default: false,
  },
  [FeatureFlagKeys.enablePlgTrialOnSignUp]: {
    default: false,
  },
  [FeatureFlagKeys.enableGoogleWorkspaceScim]: {
    default: false,
  },
  [FeatureFlagKeys.enableGroupMapping]: {
    default: false,
  },
  [FeatureFlagKeys.enableCalendlyScheduler]: {
    default: false,
  },
  [FeatureFlagKeys.enablePaymentRetries]: {
    default: false,
  },
  [FeatureFlagKeys.enableBillingCoupons]: {
    default: false,
  },
} as const;

const flagPrefix = 'AMP_';

class FeatureFlagService {
  hasInitialized = false;

  initializationPromise: Promise<void> | undefined;

  flagsmith: IFlagsmith<FlagsmithFeatureValues> | undefined;

  private listeners = new Set<() => void>();

  private notifyListeners() {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[flagsmith] listener failed', error);
        webappClient.notify(error as Error);
      }
    });
  }

  public subscribe(listener: () => void) {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  private isReady(): this is { flagsmith: IFlagsmith<FlagsmithFeatureValues> } {
    return this.hasInitialized;
  }

  private handleFlagsmithError(error: Error) {
    // eslint-disable-next-line no-console
    console.warn('[flagsmith] SDK error:', error);
    webappClient.notify(error);
  }

  private async initializeFlagsmith() {
    try {
      const flagsmithModule = await import('flagsmith');
      this.flagsmith = flagsmithModule.default as IFlagsmith<FlagsmithFeatureValues>;

      await this.flagsmith.init({
        environmentID: flagsmithConfig.environmentKey,
        cacheFlags: true,
        preventFetch: false,
        // Flagsmith 10's SSE listener calls getFlags() without a catch, so
        // init.onError does not cover realtime refresh failures.
        realtime: false,
        enableAnalytics: true,
        onChange: () => this.notifyListeners(),
        onError: (error) => this.handleFlagsmithError(error),
      });

      if (this.flagsmith.loadingState!.error) {
        throw new Error('Failed to load Flagsmith');
      }

      this.hasInitialized = true;

      this.notifyListeners();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[flagsmith] failed to initialize:', error);
    }
  }

  constructor() {
    if (flagsmithConfig.environmentKey) {
      this.initializationPromise = this.initializeFlagsmith();
    }
  }

  // Call flagsmith identify when and if the library has been loaded and
  // initialized successfully
  public async identify(user: User) {
    const { workspaceId, organizationId } = user;

    return this.initializationPromise
      ?.then(async () => {
        // At this point, the flagsmith library has been loaded and initialized
        if (!this.isReady()) {
          // eslint-disable-next-line no-console
          console.warn('Flagsmith failed to load, skipping start');
          return;
        }

        await this.flagsmith.identify(workspaceId, { organizationId, workspaceId });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('[flagsmith] identify call failed:', error);
      });
  }

  // NOTE:
  // All the following methods are being called after `identify` has been
  // awaited. If the identify call failed, the methods will return the default
  // flag values.

  // Reset the flags if the identify call was successful
  public reset() {
    if (!this.isReady()) {
      // eslint-disable-next-line no-console
      console.warn('Flagsmith failed to load, skipping reset');
      return;
    }

    this.flagsmith.logout();
    this.notifyListeners();
  }

  public isFeatureEnabled(flag: FeatureFlagKeys): boolean {
    if (!this.isReady()) {
      return featureFlagsDefaults[flag].default;
    }

    return this.flagsmith.hasFeature(flag, { fallback: featureFlagsDefaults[flag].default });
  }

  public isDestinationFeatureEnabled(
    destinationType: string,
    flag: DestinationFeatureFlags,
  ): boolean {
    const flagDefault = destinationFeatureFlagsDefaults[flag];

    if (!this.isReady()) {
      return flagDefault.payload[destinationType] === true;
    }

    const value = this.flagsmith.getValue(flag, {
      json: true,
      fallback: flagDefault.payload,
    });

    return value?.[destinationType] === true;
  }

  public getAllFeatureFlags(): Record<string | FeatureFlagKeys, boolean> {
    if (!this.isReady()) {
      return Object.fromEntries(
        Object.entries(featureFlagsDefaults)
          .filter(([key]) => !destinationFeatureFlagKeys.includes(key as DestinationFeatureFlags))
          .map(([key, value]) => [`${flagPrefix}${key}`, value.default]),
      );
    }

    return Object.fromEntries(
      Object.entries(this.flagsmith.getAllFlags())
        .filter(([key]) => !destinationFeatureFlagKeys.includes(key as DestinationFeatureFlags))
        .map(([key, flag]) => [`${flagPrefix}${key}`, flag.enabled]),
    );
  }

  public getAllDestinationFeatureFlags(destinationType: string): Record<string, boolean> {
    if (!this.isReady()) {
      return Object.fromEntries(
        Object.entries(destinationFeatureFlagsDefaults)
          .filter(([key]) => destinationFeatureFlagKeys.includes(key as DestinationFeatureFlags))
          .map(([key, value]) => [`${flagPrefix}${key}`, value.default]),
      );
    }

    return Object.fromEntries(
      Object.entries(this.flagsmith.getAllFlags())
        .filter(([key]) => destinationFeatureFlagKeys.includes(key as DestinationFeatureFlags))
        .map(([flagId]) => [
          `${flagPrefix}${flagId}`,
          this.isDestinationFeatureEnabled(destinationType, flagId as DestinationFeatureFlags),
        ]),
    );
  }

  public getFeatureValue<T extends FeatureFlagsWithPayload>(flag: T): FeatureFlagPayloads[T] {
    const flagDefaults = featureFlagsDefaults[flag];
    const fallback = flagDefaults.payload as FeatureFlagPayloads[T];

    if (!this.isReady()) {
      return fallback;
    }

    const flagsmith = this.flagsmith as IFlagsmith<Record<string, FeatureFlagPayloads[T]>>;

    return flagsmith.getValue(flag, { json: true, fallback }) as unknown as FeatureFlagPayloads[T];
  }
}

export const experiment = new FeatureFlagService();
